import { brand } from "../../../../../config/theme";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { VehicleSocket } from "../../../../../store/admin/vehiclesSocketStore";
import {
  useVehicleRouteTrackingStore,
  useIsTracking,
  useTrackingPointsCount,
  useTrackingMeta,
  ROUTE_STATUS_COLORS,
} from "../../../../../store/admin/vehicleRouteTrackingStore";
import { useAlert } from "../../../../../context/AlertContext";
import { useRoutesStore } from "../../../../../store/admin/routesStore";
import useIsMonitor from "../../../../../hooks/useIsMonitor";
import { useRouteForm } from "../../../../admin/components/sections/routesComponents";
import { RouteModal } from "../../../../admin/components/sections/routesComponents";
import type { ThemeClasses, RouteFormValues } from "../../../../admin/components/sections/routesComponents/types";

interface RouteContentProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
}

const ROUTE_COLOR = brand.primary;

const formatRangeTime = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export default function RouteContent({
  vehicle,
  isDarkMode,
}: RouteContentProps) {
  const { loadHistory, stopTracking, getTracking } =
    useVehicleRouteTrackingStore();

  const isCurrentlyTracking = useIsTracking(vehicle.id);
  const trackingPointsCount = useTrackingPointsCount(vehicle.id);
  const { loading, error, from, to } = useTrackingMeta(vehicle.id);

  const [enabled, setEnabled] = useState(isCurrentlyTracking);
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const { showAlert } = useAlert();
  const { isMonitor } = useIsMonitor();
  const createRoute = useRoutesStore((state) => state.createRoute);
  const { t } = useTranslation("lite");
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const {
    form,
    routePoints,
    routeDistance,
    estimatedTime,
    hasRealChanges,
    handleMapPointsChange,
    handleCoordinateEdit,
    handleAddressEdit,
    handleAddPoint,
    handleRemovePoint,
    handleRouteCalculated,
    handleAxisCalculated,
    handleBufferChange,
    resetForm,
    prepareRouteData,
  } = useRouteForm({ isEdit: false, editRoute: null });

  useEffect(() => {
    setEnabled(isCurrentlyTracking);
  }, [vehicle.id, isCurrentlyTracking]);

  const handleToggle = () => {
    if (enabled) {
      stopTracking(vehicle.id);
      setEnabled(false);
    } else {
      setEnabled(true);
      loadHistory(
        vehicle.id,
        vehicle.vehicle?.name || vehicle.fullId,
        ROUTE_COLOR,
        undefined,
        vehicle.brandName,
        vehicle.brandModel,
      );
    }
  };

  const handleReload = () => {
    loadHistory(
      vehicle.id,
      vehicle.vehicle?.name || vehicle.fullId,
      ROUTE_COLOR,
      undefined,
      vehicle.brandName,
      vehicle.brandModel,
    );
  };

  const handleOpenRouteModal = useCallback(() => {
    const tracking = getTracking(vehicle.id);
    if (!tracking || tracking.points.length < 2) return;

    handleMapPointsChange(
      tracking.points.map((p) => ({ lat: p.lat, lng: p.lng }))
    );
    setShowRouteModal(true);
  }, [vehicle.id, getTracking, handleMapPointsChange]);

  const handleCloseRouteModal = useCallback(() => {
    setShowRouteModal(false);
    setLoadingSubmit(false);
    resetForm();
  }, [resetForm]);

  const handleCreateRouteSubmit = useCallback(
    async (data: RouteFormValues) => {
      setLoadingSubmit(true);
      const companyId = localStorage.getItem("companyId");
      const payload = {
        ...prepareRouteData(data),
        company_id: companyId || "",
      };

      const result = await createRoute(payload);

      if (result.success) {
        showAlert({
          title: t("routeContent.routeCreatedTitle", { defaultValue: "Ruta creada" }),
          text: t("routeContent.routeCreatedText", { defaultValue: "Ruta creada correctamente" }),
          icon: "success",
        });
        handleCloseRouteModal();
      } else {
        showAlert({
          title: t("routeContent.routeErrorTitle", { defaultValue: "Error" }),
          text: result.error || t("routeContent.routeErrorText", { defaultValue: "Error al crear la ruta" }),
          icon: "error",
        });
      }
      setLoadingSubmit(false);
    },
    [prepareRouteData, createRoute, showAlert, t, handleCloseRouteModal]
  );

  const stateLegend: { status: keyof typeof ROUTE_STATUS_COLORS; label: string }[] = [
    { status: "driving", label: t("routeContent.stateDriving", { defaultValue: "En movimiento" }) },
    { status: "ralenti", label: t("routeContent.stateIdle", { defaultValue: "Ralentí" }) },
    { status: "stopped", label: t("routeContent.stateStopped", { defaultValue: "Detenido" }) },
  ];

  return (
    <div className={`p-3 border-t ${borderColor}`}>
      <h4 className="text-primary-500 font-semibold text-xs mb-2">{t("routeContent.title")}</h4>

      <div className="flex items-center gap-3">
        <span
          className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
        >
          {t("routeContent.showRoute", { defaultValue: "Mostrar recorrido del día" })}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={handleToggle}
          className={`
            relative inline-flex h-5 w-10 items-center rounded-full transition-colors duration-200
            ${enabled ? "bg-primary-500" : "bg-gray-300"}
          `}
        >
          <span
            className={`
              inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200
              ${enabled ? "translate-x-5" : "translate-x-1"}
            `}
          />
        </button>
      </div>

      {enabled && (
        <>
          {loading && (
            <div className="mt-4 flex items-center gap-2">
              <div
                className={`animate-spin h-4 w-4 border-2 border-t-transparent rounded-full ${
                  isDarkMode ? "border-gray-400" : "border-gray-500"
                }`}
              ></div>
              <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {t("routeContent.loadingPoints", { defaultValue: "Obteniendo recorrido..." })}
              </span>
            </div>
          )}

          {!loading && error && (
            <div className="mt-3">
              <p className="text-xs text-red-500">
                {t("routeContent.loadError", { defaultValue: "No se pudo cargar el recorrido" })}
              </p>
              <button
                onClick={handleReload}
                className="mt-2 px-3 py-1.5 rounded text-[10px] font-medium bg-primary-500 text-black hover:bg-primary-400 transition-colors"
              >
                {t("routeContent.retry", { defaultValue: "Reintentar" })}
              </button>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Resumen del recorrido */}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className={`rounded p-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-100"}`}>
                  <p className={`text-[10px] ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {t("routeContent.pointsRecorded")}
                  </p>
                  <p className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {trackingPointsCount}
                  </p>
                </div>
                <div className={`rounded p-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-100"}`}>
                  <p className={`text-[10px] ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {t("routeContent.timeRange", { defaultValue: "Rango horario" })}
                  </p>
                  <p className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {from && to ? `${formatRangeTime(from)} - ${formatRangeTime(to)}` : "—"}
                  </p>
                </div>
              </div>

              {/* Leyenda de estados */}
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                {stateLegend.map((s) => (
                  <div key={s.status} className="flex items-center gap-1">
                    <span
                      className="inline-block w-3 h-1.5 rounded-full"
                      style={{ backgroundColor: ROUTE_STATUS_COLORS[s.status] }}
                    />
                    <span className={`text-[10px] ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                      {s.label}
                    </span>
                  </div>
                ))}
              </div>

              {trackingPointsCount === 0 && (
                <p className={`text-[10px] mt-3 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                  {t("routeContent.noPointsToday", { defaultValue: "Sin reportes para el recorrido de hoy" })}
                </p>
              )}

              {/* Temporalmente oculto
              {!isMonitor && (
                <button
                  onClick={handleOpenRouteModal}
                  disabled={trackingPointsCount < 2}
                  className={`w-full px-3 py-2 rounded text-[10px] font-medium bg-primary-500 text-black mt-3 transition-colors ${
                    trackingPointsCount < 2
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-primary-400"
                  }`}
                >
                  {t("routeContent.createRoute", { defaultValue: "Crear ruta con estos puntos" })}
                </button>
              )}
              {trackingPointsCount < 2 && trackingPointsCount > 0 && (
                <p
                  className={`text-[10px] mt-1 ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  {t("routeContent.needAtLeast2Points")}
                </p>
              )}
              */}
              {/* El aviso de «al menos 2 puntos» es del botón de arriba: queda
                  oculto con él (antes salía solo, con un punto, sin decir para
                  qué hacían falta dos). */}
            </>
          )}
        </>
      )}

      <RouteModal
        isOpen={showRouteModal}
        isEdit={false}
        loadingSubmit={loadingSubmit}
        form={form}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        routePoints={routePoints}
        routeDistance={routeDistance}
        estimatedTime={estimatedTime}
        hasRealChanges={hasRealChanges}
        onCoordinateEdit={handleCoordinateEdit}
        onAddressEdit={handleAddressEdit}
        onAddPoint={handleAddPoint}
        onRemovePoint={handleRemovePoint}
        onMapPointsChange={handleMapPointsChange}
        onRouteCalculated={handleRouteCalculated}
        onAxisCalculated={handleAxisCalculated}
        onCorridorBufferChange={handleBufferChange}
        onSubmit={handleCreateRouteSubmit}
        onClose={handleCloseRouteModal}
      />
    </div>
  );
}
